// src/components/PublicDriverLocationMap.js
import React, { useState, useEffect, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Configure axios for the public map
const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const baseUrl = apiUrl.endsWith('/api') ? apiUrl : `${apiUrl}/api`;
axios.defaults.baseURL = baseUrl;
axios.defaults.withCredentials = true;

const GOOGLE_MAPS_LIBRARIES = ['places'];

const mapContainerStyle = {
  width: '100%',
  height: '80vh'
};

const defaultCenter = {
  lat: 52.2053, // UK center
  lng: 0.1218
};

const PublicDriverLocationMap = () => {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES
  });

  // Load driver locations
  const loadDriverLocations = useCallback(async () => {
    try {
      console.log('🔄 Loading driver locations...');
      const response = await axios.get('/admin/public/drivers/locations');
      
      if (response.data.success) {
        setDrivers(response.data.drivers);
        setLastUpdated(new Date());
        console.log(`✅ Loaded ${response.data.drivers.length} driver locations`);
      } else {
        throw new Error(response.data.error || 'Failed to load driver locations');
      }
    } catch (error) {
      console.error('❌ Error loading driver locations:', error);
      setError('Failed to load driver locations. Please try again.');
      toast.error('Failed to load driver locations');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load driver locations when component mounts
  useEffect(() => {
    if (isLoaded) {
      loadDriverLocations();
    }
  }, [isLoaded, loadDriverLocations]);

  // Auto-refresh driver locations every 30 seconds
  useEffect(() => {
    if (!isLoaded) return;

    const refreshInterval = setInterval(() => {
      console.log('🔄 Auto-refreshing driver locations...');
      loadDriverLocations();
    }, 30000); // 30 seconds

    return () => {
      clearInterval(refreshInterval);
    };
  }, [isLoaded, loadDriverLocations]);

  // Create driver icon
  const createDriverIcon = (driver) => {
    if (driver.logo) {
      return {
        url: driver.logo,
        scaledSize: new window.google.maps.Size(40, 40),
        anchor: new window.google.maps.Point(20, 20)
      };
    }
    return null; // Use default marker
  };

  // Handle marker click
  const handleMarkerClick = (driver) => {
    setSelectedDriver(driver);
  };

  // Handle info window close
  const handleInfoWindowClose = () => {
    setSelectedDriver(null);
  };

  // Format last updated time
  const formatLastUpdated = () => {
    if (!lastUpdated) return '';
    return lastUpdated.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loadError) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <h4>Map Loading Error</h4>
          <p>Failed to load Google Maps. Please check your internet connection and try again.</p>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading map...</span>
          </div>
          <p className="mt-2">Loading Google Maps...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h4>Live Driver Locations</h4>
              <p className="text-muted mb-0">
                Real-time tracking of active drivers in the network
              </p>
            </div>
            <div className="text-end">
              <div className="d-flex align-items-center gap-3">
                <div className="text-muted small">
                  <i className="bi bi-people-fill me-1"></i>
                  {drivers.length} Active Drivers
                </div>
                {lastUpdated && (
                  <div className="text-muted small">
                    <i className="bi bi-clock me-1"></i>
                    Last updated: {formatLastUpdated()}
                  </div>
                )}
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={loadDriverLocations}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1"></span>
                      Loading...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-arrow-clockwise me-1"></i>
                      Refresh
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="alert alert-warning mb-3">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </div>
          )}

          <div className="card">
            <div className="card-body p-0">
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={defaultCenter}
                zoom={6}
                options={{
                  streetViewControl: false,
                  mapTypeControl: true,
                  fullscreenControl: true,
                  zoomControl: true
                }}
              >
                {drivers.map((driver) => (
                  <Marker
                    key={driver.id}
                    position={{
                      lat: driver.location.latitude,
                      lng: driver.location.longitude
                    }}
                    icon={createDriverIcon(driver)}
                    onClick={() => handleMarkerClick(driver)}
                  />
                ))}

                {selectedDriver && (
                  <InfoWindow
                    position={{
                      lat: selectedDriver.location.latitude,
                      lng: selectedDriver.location.longitude
                    }}
                    onCloseClick={handleInfoWindowClose}
                  >
                    <div style={{ padding: '10px', minWidth: '200px' }}>
                      <h6 className="mb-2">
                        {selectedDriver.logo && (
                          <img
                            src={selectedDriver.logo}
                            alt="Driver Logo"
                            style={{
                              width: '30px',
                              height: '30px',
                              borderRadius: '50%',
                              marginRight: '10px',
                              objectFit: 'cover'
                            }}
                          />
                        )}
                        {selectedDriver.name}
                      </h6>
                      <p className="mb-1">
                        <strong>Company:</strong> {selectedDriver.companyName}
                      </p>
                      <p className="mb-1">
                        <strong>Status:</strong>
                        <span className={`badge ms-1 ${
                          selectedDriver.status.profileCompleted && selectedDriver.status.licensingApproved
                            ? 'bg-success'
                            : 'bg-warning'
                        }`}>
                          {selectedDriver.status.profileCompleted && selectedDriver.status.licensingApproved
                            ? 'Fully Approved'
                            : 'Pending Approval'
                          }
                        </span>
                      </p>
                      {selectedDriver.location.address && (
                        <p className="mb-1">
                          <strong>Location:</strong> {selectedDriver.location.address}
                        </p>
                      )}
                      <p className="mb-0 text-muted small">
                        <strong>Last Updated:</strong>{' '}
                        {new Date(selectedDriver.location.lastUpdated).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: '2-digit',
                          year: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          timeZone: 'Europe/London'
                        })}
                      </p>
                    </div>
                  </InfoWindow>
                )}
              </GoogleMap>
            </div>
          </div>

          <div className="mt-3">
            <div className="alert alert-info">
              <h6 className="mb-2">
                <i className="bi bi-info-circle me-2"></i>
                About This Map
              </h6>
              <p className="mb-1">
                This map shows the real-time locations of active drivers in the Taxi-Local network.
                Driver positions are updated automatically every 30 seconds.
              </p>
              <p className="mb-0">
                <strong>Note:</strong> This is a public view. For administrative functions, please log in to the system.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicDriverLocationMap;
